// Postmenopause "Vitality Report" — single container with `variant="home"`
// (compact teaser) and `variant="care"` (full report with 4 sections, HRT
// row, and download/send actions). Reuses existing pure helpers only —
// no new server fn, no new DB column.
//
// IMPORTANT scope rules for this build:
//   - PDF generator is UNCHANGED. The Download/Send actions are wired to
//     the EXISTING generateAnnualWellnessSummaryPDF but labeled honestly
//     (NOT "Vitality Report") — the dedicated Vitality PDF lands in the
//     next phase.
//   - The shared peri/meno REPORT_MILESTONES strip in KnowMenoApp.tsx is
//     left alone. The postmeno milestone copy below is local-only.

import { useEffect, useState } from "react";
import SendToDoctorModal from "./SendToDoctorModal";
import { generateAnnualWellnessSummaryPDF } from "./annualWellnessSummary";
import {
  loadVitalityScoreForCurrentUser,
  loadVitalityScoreSeriesForCurrentUser,
  type VitalityScore,
  type VitalityScoreSeriesPoint,
} from "./vitalityScore";
import {
  fetchWearableMetrics,
  summarizeForToday,
  type WearableTodaySummary,
} from "./wearableMetrics";
import { fitnessAgeFromVO2max, ageFromDob, type FitnessAgeResult } from "./fitnessAge";
import { groupByMarker, computeTrend, type MarkerPoint } from "./labTrends";
import { LAB_MARKERS, getRange, type MarkerKey, type LifeStage } from "./labRanges";
import { statusFor, type ScreeningStatus } from "./screeningStatus";
import { isOnHrt, getHrtName, getHrtStartDate } from "./hrtStatus";
import { fetchReportData, buildSymptomDirections, type DirectionRow } from "./reportData";
import { supabase } from "@/integrations/supabase/client";
import type { LabUploadRow } from "@/lib/labs.functions";
import type { HealthScreeningRow } from "@/lib/screenings.functions";

// Brand colors (mirror KnowMenoApp.tsx constants).
const PD = "#4B1F4F";
const PU = "#FF5C7A";
const WH = "#ffffff";
const GD = "#2E2A2C";
const GR = "#7A6F73";
const BG = "#FAF7F5";
const BORDER = "#E8D5DA";

// Local milestone arc for the POSTMENO vitality path. Do NOT use this for
// peri/meno — that arc lives in KnowMenoApp.REPORT_MILESTONES.
const VITALITY_REPORT_MILESTONES = [
  { day: 7,  emoji: "📅", title: "First patterns" },
  { day: 14, emoji: "🔬", title: "Basic Vitality Report unlocks" },
  { day: 30, emoji: "📈", title: "Trends become meaningful" },
  { day: 90, emoji: "🎯", title: "Comprehensive Vitality Report" },
];

function nextVitalityMilestone(loggedDays: number): { prefix: string; body: string } {
  if (loggedDays >= 90) return { prefix: "Comprehensive Vitality Report", body: "90-day baseline complete" };
  if (loggedDays >= 30) return { prefix: "Next milestone: Day 90", body: "Comprehensive Vitality Report" };
  if (loggedDays >= 14) return { prefix: "Next milestone: Day 30", body: "Trends become meaningful" };
  if (loggedDays >= 7)  return { prefix: "Next milestone: Day 14", body: "Basic Vitality Report unlocks" };
  return { prefix: "Next milestone: Day 7", body: "Your first patterns appear" };
}

// ------------------------------------------------------------------ helpers

function Sparkline({ points, color = WH }: { points: VitalityScoreSeriesPoint[]; color?: string }) {
  const w = 120, h = 28;
  const vals = points.map((p) => (typeof p.score === "number" ? p.score : null));
  const valid = vals.filter((v): v is number => v != null);
  if (valid.length < 2) return <div style={{ width: w, height: h }} />;
  const min = Math.min(...valid);
  const max = Math.max(...valid);
  const span = Math.max(1, max - min);
  const step = w / (vals.length - 1);
  let d = "";
  vals.forEach((v, i) => {
    if (v == null) return;
    const x = i * step;
    const y = h - ((v - min) / span) * h;
    d += (d ? " L" : "M") + `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  return (
    <svg width={w} height={h} style={{ display: "block" }}>
      <path d={d} fill="none" stroke={color} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function MetricTile({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div style={{ background: BG, borderRadius: 12, padding: "12px 14px", flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 10, color: GR, letterSpacing: "0.06em", textTransform: "uppercase", fontWeight: 500, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 500, color: GD, lineHeight: 1.2 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: GR, marginTop: 3, lineHeight: 1.4 }}>{sub}</div>}
    </div>
  );
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ fontSize: 11, color: GR, letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 500, margin: "18px 0 8px" }}>
      {children}
    </div>
  );
}

const SCREENING_LABELS: Record<string, string> = {
  mammogram: "Mammogram",
  dexa: "DEXA scan",
  lipid_panel: "Lipid panel",
  a1c: "A1c",
  colonoscopy: "Colonoscopy",
  pap_hpv: "Pap / HPV",
  blood_pressure: "Blood pressure",
  eye_exam: "Eye exam",
};

const STATUS_LABEL: Record<ScreeningStatus, string> = {
  overdue: "Overdue",
  due_soon: "Due soon",
  up_to_date: "Up to date",
  never_logged: "Not logged",
};

const STATUS_COLOR: Record<ScreeningStatus, string> = {
  overdue: "#C0392B",
  due_soon: "#C49A1F",
  up_to_date: "#2E7D5B",
  never_logged: GR,
};

// ------------------------------------------------------------------ hero

function VitalityHero({
  milestoneDay,
  loggedDays,
  compact,
}: {
  milestoneDay: number;
  loggedDays: number;
  compact: boolean;
}) {
  const d = Math.min(Math.max(1, milestoneDay || 1), 90);
  const ld = Math.min(90, Math.max(0, loggedDays));
  const pct = Math.min(100, Math.round((ld / 90) * 100));
  const dashArray = 2 * Math.PI * 32;
  const dashOffset = dashArray * (1 - pct / 100);
  const ms = nextVitalityMilestone(ld);
  return (
    <div style={{ background: `linear-gradient(135deg,${PD},${PU})`, borderRadius: 22, padding: compact ? "20px 20px 18px" : "22px 22px 20px", color: WH, marginBottom: compact ? 0 : 14 }}>
      <div style={{ fontSize: 10, opacity: 0.7, letterSpacing: "0.12em", textTransform: "uppercase", fontWeight: 500, marginBottom: compact ? 14 : 16 }}>
        Your Vitality Report · building live
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 18, marginBottom: compact ? 14 : 16 }}>
        <div style={{ position: "relative", width: 74, height: 74, flexShrink: 0 }}>
          <svg width="74" height="74" style={{ transform: "rotate(-90deg)" }}>
            <circle cx="37" cy="37" r="32" fill="none" stroke="rgba(255,255,255,0.18)" strokeWidth="6" />
            <circle cx="37" cy="37" r="32" fill="none" stroke={WH} strokeWidth="6" strokeDasharray={dashArray} strokeDashoffset={dashOffset} strokeLinecap="round" />
          </svg>
          <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
            <div style={{ fontSize: 18, fontWeight: 500, lineHeight: 1 }}>{pct}%</div>
            <div style={{ fontSize: 9, opacity: 0.75, marginTop: 2 }}>{ld >= 90 ? "complete" : "to baseline"}</div>
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: compact ? 16 : 17, fontWeight: 500, marginBottom: 4, lineHeight: 1.3, letterSpacing: "-0.01em" }}>Day {d} · Tracking</div>
          <div style={{ fontSize: 12, opacity: 0.85, lineHeight: 1.55 }}>
            <strong style={{ fontWeight: 500 }}>{ms.prefix}</strong>
            {ms.body ? <> · {ms.body}</> : null}
          </div>
        </div>
      </div>
      <div style={{ display: "flex", gap: 4 }}>
        {VITALITY_REPORT_MILESTONES.map((m) => {
          const reached = ld >= m.day;
          return (
            <div key={m.day} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
              <div style={{ width: compact ? 24 : 28, height: compact ? 24 : 28, borderRadius: "50%", background: reached ? PU : "rgba(255,255,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: compact ? 12 : 14, color: WH, opacity: reached ? 1 : 0.5 }}>
                {reached ? m.emoji : ""}
              </div>
              <div style={{ fontSize: compact ? 8 : 9, opacity: reached ? 0.9 : 0.5, fontWeight: 500 }}>{`D${m.day}`}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ data hooks

function useVitality() {
  const [score, setScore] = useState<VitalityScore | null>(null);
  const [series, setSeries] = useState<VitalityScoreSeriesPoint[]>([]);
  useEffect(() => {
    let active = true;
    (async () => {
      const [s, ss] = await Promise.all([
        loadVitalityScoreForCurrentUser(),
        loadVitalityScoreSeriesForCurrentUser(14),
      ]);
      if (!active) return;
      setScore(s);
      setSeries(ss);
    })().catch(() => {});
    return () => { active = false; };
  }, []);
  return { score, series };
}

function useWearableSummary() {
  const [summary, setSummary] = useState<WearableTodaySummary | null>(null);
  useEffect(() => {
    let active = true;
    (async () => {
      const { data } = await supabase.auth.getUser();
      const uid = data?.user?.id;
      if (!uid) return;
      const rows = await fetchWearableMetrics(uid, 60);
      if (!active) return;
      setSummary(summarizeForToday(rows));
    })().catch(() => {});
    return () => { active = false; };
  }, []);
  return summary;
}

function useSymptomDirections(user: any) {
  const [dirs, setDirs] = useState<DirectionRow[] | null>(null);
  useEffect(() => {
    let active = true;
    (async () => {
      const data = await fetchReportData(user);
      if (!active) return;
      setDirs(buildSymptomDirections(data).directions);
    })().catch(() => {});
    return () => { active = false; };
  }, [user?.id]);
  return dirs;
}

// ------------------------------------------------------------------ section renderers

function RecoverySection({ score, series }: { score: VitalityScore | null; series: VitalityScoreSeriesPoint[] }) {
  if (!score) {
    return <div style={{ fontSize: 13, color: GR, lineHeight: 1.5 }}>Loading recovery signals…</div>;
  }
  if (score.status !== "ok" || score.score == null) {
    return (
      <div style={{ fontSize: 13, color: GR, lineHeight: 1.55 }}>
        {score.lead}
      </div>
    );
  }
  const top = score.contributions[0];
  return (
    <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
      <div style={{ flexShrink: 0, width: 64, height: 64, borderRadius: "50%", background: `linear-gradient(135deg,${PD},${PU})`, color: WH, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column" }}>
        <div style={{ fontSize: 20, fontWeight: 500, lineHeight: 1 }}>{score.score}</div>
        <div style={{ fontSize: 8, opacity: 0.85, marginTop: 2, letterSpacing: "0.06em" }}>RECOVERY</div>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, color: GD, lineHeight: 1.5, marginBottom: 6 }}>{score.lead}</div>
        {top && (
          <div style={{ fontSize: 11, color: GR, lineHeight: 1.45 }}>
            {top.label}: {top.detail}
          </div>
        )}
        {series.length >= 2 && (
          <div style={{ marginTop: 8 }}>
            <Sparkline points={series} color={PU} />
          </div>
        )}
      </div>
    </div>
  );
}

function LongevitySection({ user, summary }: { user: any; summary: WearableTodaySummary | null }) {
  const vo2 = summary?.vo2Max?.value ?? null;
  const age = ageFromDob(user?.dob);
  let fa: FitnessAgeResult | null = null;
  if (vo2 != null && age != null) fa = fitnessAgeFromVO2max(vo2, age);

  return (
    <div style={{ display: "flex", gap: 8 }}>
      <MetricTile
        label="VO2max"
        value={vo2 != null ? `${vo2.toFixed(1)}` : "—"}
        sub={vo2 != null ? "ml/kg/min" : "Connect a wearable that reports VO2max"}
      />
      <MetricTile
        label="Fitness age"
        value={fa ? `${fa.fitnessAge} yrs` : "—"}
        sub={
          fa
            ? `${fa.deltaYears === 0 ? "Matches" : fa.deltaYears > 0 ? `${fa.deltaYears} yrs younger` : `${Math.abs(fa.deltaYears)} yrs older`} than your age`
            : vo2 == null
              ? "Needs VO2max"
              : age == null
                ? "Add your birth year in Settings"
                : "—"
        }
      />
    </div>
  );
}

function HealthspanSection({
  user,
  labUploads,
  screenings,
}: {
  user: any;
  labUploads: LabUploadRow[];
  screenings: HealthScreeningRow[];
}) {
  const stage = (user?.stage || user?.lifeStage || "Postmenopause") as LifeStage;
  const onHrt = isOnHrt(user);
  const grouped = groupByMarker(labUploads as any);
  const markersWithData = LAB_MARKERS.filter((m) => (grouped[m.key]?.length ?? 0) > 0);
  const sortedScreenings = [...screenings].sort((a, b) => {
    const order: ScreeningStatus[] = ["overdue", "due_soon", "up_to_date", "never_logged"];
    return order.indexOf(statusFor(a)) - order.indexOf(statusFor(b));
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ background: BG, borderRadius: 12, padding: "12px 14px" }}>
        <div style={{ fontSize: 11, color: GR, letterSpacing: "0.06em", textTransform: "uppercase", fontWeight: 500, marginBottom: 8 }}>Labs</div>
        {markersWithData.length === 0 ? (
          <div style={{ fontSize: 12, color: GR, lineHeight: 1.5 }}>No labs uploaded yet — add results to see hormone + thyroid trends.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {markersWithData.map((m) => {
              const points: MarkerPoint[] = grouped[m.key as MarkerKey];
              const latest = points[points.length - 1];
              const trend = computeTrend(points.map((p) => p.value));
              const range = getRange(m.key as MarkerKey, stage, onHrt, user?.hrtType);
              const arrow = trend === "up" ? "↑" : trend === "down" ? "↓" : trend === "stable" ? "→" : "";
              const inRange =
                (range.low == null || latest.value >= range.low) &&
                (range.high == null || latest.value <= range.high);
              return (
                <div key={m.key} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: GD }}>
                  <span style={{ fontWeight: 500 }}>{m.displayName}</span>
                  <span style={{ color: inRange ? "#2E7D5B" : "#C49A1F" }}>
                    {latest.value} {latest.unit} {arrow}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <div style={{ background: BG, borderRadius: 12, padding: "12px 14px" }}>
        <div style={{ fontSize: 11, color: GR, letterSpacing: "0.06em", textTransform: "uppercase", fontWeight: 500, marginBottom: 8 }}>Screenings</div>
        {sortedScreenings.length === 0 ? (
          <div style={{ fontSize: 12, color: GR, lineHeight: 1.5 }}>No screenings logged yet.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {sortedScreenings.slice(0, 6).map((s) => {
              const st = statusFor(s);
              return (
                <div key={s.id} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: GD }}>
                  <span style={{ fontWeight: 500 }}>{SCREENING_LABELS[s.screening_type] || s.screening_type}</span>
                  <span style={{ color: STATUS_COLOR[st] }}>{STATUS_LABEL[st]}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function SymptomsLight({ dirs }: { dirs: DirectionRow[] | null }) {
  if (!dirs) return <div style={{ fontSize: 13, color: GR }}>Loading symptom patterns…</div>;
  const noted = dirs
    .filter((d) => d.lastDays > 0)
    .sort((a, b) => b.lastAvg - a.lastAvg)
    .slice(0, 4);
  if (noted.length === 0) {
    return <div style={{ fontSize: 13, color: GR, lineHeight: 1.55 }}>Nothing logged in the last 30 days. Tracking what you're still noticing helps your care team.</div>;
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ fontSize: 12, color: GR, lineHeight: 1.5, marginBottom: 2 }}>Tracking what you're still noticing (last 30 vs prior 30):</div>
      {noted.map((d) => {
        const arrow = d.dir === "improving" ? "↓" : d.dir === "worsening" ? "↑" : "→";
        const color = d.dir === "improving" ? "#2E7D5B" : d.dir === "worsening" ? "#C0392B" : GR;
        return (
          <div key={d.name} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: GD }}>
            <span style={{ fontWeight: 500 }}>{d.name}</span>
            <span style={{ color }}>{arrow} {d.dir}{d.pct ? ` (${d.pct > 0 ? "+" : ""}${d.pct}%)` : ""}</span>
          </div>
        );
      })}
    </div>
  );
}

function HrtRow({ user }: { user: any }) {
  const on = isOnHrt(user);
  if (!on) {
    return (
      <div style={{ background: BG, borderRadius: 12, padding: "12px 14px", fontSize: 12, color: GR, lineHeight: 1.5 }}>
        Not currently on HRT.
      </div>
    );
  }
  const name = getHrtName(user) || "HRT";
  const start = getHrtStartDate(user);
  let duration = "";
  if (start) {
    const startMs = new Date(start).getTime();
    if (Number.isFinite(startMs)) {
      const months = Math.max(0, Math.round((Date.now() - startMs) / (30.4375 * 86400000)));
      duration = months >= 12 ? `${(months / 12).toFixed(1)} yrs` : `${months} mo`;
    }
  }
  return (
    <div style={{ background: BG, borderRadius: 12, padding: "12px 14px" }}>
      <div style={{ fontSize: 13, color: GD, fontWeight: 500 }}>{name}</div>
      <div style={{ fontSize: 12, color: GR, marginTop: 3 }}>
        {start && duration
          ? `On HRT for ${duration}`
          : "On HRT — add your start date in Settings to show duration"}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ component

type Props = {
  variant: "home" | "care";
  user: any;
  isPremium: boolean;
  onUpgrade: () => void;
  milestoneDay?: number;
  loggedDays?: number;
  // Care-only:
  labUploads?: LabUploadRow[];
  screenings?: HealthScreeningRow[];
  onNavigateToCare?: () => void;
};

export function VitalityReportCard({
  variant,
  user,
  isPremium,
  onUpgrade,
  milestoneDay = 1,
  loggedDays = 0,
  labUploads = [],
  screenings = [],
  onNavigateToCare,
}: Props) {
  const { score, series } = useVitality();
  const wearable = useWearableSummary();
  const symptomDirs = useSymptomDirections(variant === "care" ? user : null);

  const [busy, setBusy] = useState(false);
  const [showSend, setShowSend] = useState(false);

  const generatePdf = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await generateAnnualWellnessSummaryPDF(user, { labUploads, screenings });
    } catch (e) {
      console.error("[VitalityReport] download failed", e);
      alert("Couldn't build your Vitality Report. Please try again.");
    } finally {
      setBusy(false);
    }
  };
  const sendToDoctor = () => {
    if (!isPremium) { onUpgrade(); return; }
    setShowSend(true);
  };

  // ---------- HOME VARIANT: compact teaser ----------
  if (variant === "home") {
    const vo2 = wearable?.vo2Max?.value ?? null;
    const age = ageFromDob(user?.dob);
    const fa = vo2 != null && age != null ? fitnessAgeFromVO2max(vo2, age) : null;
    const recoveryLabel = score?.status === "ok" && score.score != null
      ? `${score.score}`
      : "—";
    return (
      <div style={{ marginBottom: 18 }}>
        <VitalityHero milestoneDay={milestoneDay} loggedDays={loggedDays} compact />
        <div style={{ background: WH, border: `0.5px solid ${BORDER}`, borderRadius: 16, borderTopLeftRadius: 0, borderTopRightRadius: 0, marginTop: -1, padding: "14px 16px" }}>
          <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
            <MetricTile
              label="Recovery"
              value={recoveryLabel}
              sub={score?.status === "ok" ? "today" : score?.status === "building_baseline" ? "building baseline" : "no data yet"}
            />
            <MetricTile
              label="VO2max"
              value={vo2 != null ? vo2.toFixed(1) : "—"}
              sub={vo2 != null ? "ml/kg/min" : "Connect a wearable"}
            />
            <MetricTile
              label="Fitness age"
              value={fa ? `${fa.fitnessAge}` : "—"}
              sub={fa
                ? (fa.deltaYears === 0 ? "matches age" : fa.deltaYears > 0 ? `${fa.deltaYears} yrs younger` : `${Math.abs(fa.deltaYears)} yrs older`)
                : vo2 == null ? "needs VO2max" : age == null ? "add birth year" : "—"}
            />
          </div>
          <button
            onClick={() => onNavigateToCare?.()}
            style={{ width: "100%", padding: "11px", borderRadius: 12, background: PD, border: "none", color: WH, fontSize: 13, fontWeight: 500, cursor: "pointer" }}
          >
            View full Vitality Report →
          </button>
        </div>
      </div>
    );
  }

  // ---------- CARE VARIANT: full report ----------
  return (
    <>
      <SendToDoctorModal
        open={showSend}
        onClose={() => setShowSend(false)}
        onGeneratePDF={generatePdf}
        user={user}
        daysTracked={Math.max(1, loggedDays || milestoneDay || 1)}
      />
      <div style={{ marginBottom: 14 }}>
        <VitalityHero milestoneDay={milestoneDay} loggedDays={loggedDays} compact={false} />

        <div style={{ background: WH, border: `0.5px solid ${BORDER}`, borderRadius: 16, padding: "16px 18px" }}>
          <SectionHeader>Recovery</SectionHeader>
          <RecoverySection score={score} series={series} />

          <SectionHeader>Longevity</SectionHeader>
          <LongevitySection user={user} summary={wearable} />

          <SectionHeader>Healthspan</SectionHeader>
          <HealthspanSection user={user} labUploads={labUploads} screenings={screenings} />

          <SectionHeader>Symptoms you're still noticing</SectionHeader>
          <SymptomsLight dirs={symptomDirs} />

          <SectionHeader>HRT</SectionHeader>
          <HrtRow user={user} />

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 18 }}>
            <button
              onClick={generatePdf}
              disabled={busy}
              style={{ gridColumn: "span 2", padding: "12px", borderRadius: 12, background: PD, border: "none", color: WH, fontSize: 13, fontWeight: 500, cursor: busy ? "wait" : "pointer", opacity: busy ? 0.7 : 1 }}
            >
              {busy ? "Building…" : "↓ Download Vitality Report"}
            </button>
            <button
              onClick={sendToDoctor}
              style={{ gridColumn: "span 2", padding: "12px", borderRadius: 12, background: WH, border: `1.5px solid ${PD}`, color: PD, fontSize: 13, fontWeight: 500, cursor: "pointer" }}
            >
              {isPremium ? "Send summary to my doctor →" : "Unlock send-to-doctor →"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default VitalityReportCard;
